import React, { useState } from 'react'
import { ChevronRight, Code, Users, Megaphone, Headphones } from 'lucide-react'
import { Role } from '../types'

interface RoleSelectionProps {
  onRoleSelect: (role: Role) => void
}

const roles: Role[] = [
  {
    id: 'software-developer',
    title: 'Software Developer',
    description: 'Technical interviews and coding assessments',
    icon: '/software.png',
    color: 'bg-pink-400'
  },
  {
    id: 'sales-manager',
    title: 'Sales Manager',
    description: 'Sales strategy and client relationship discussions',
    icon: '/sales.png',
    color: 'bg-yellow-400'
  },
  {
    id: 'marketing-specialist',
    title: 'Marketing Specialist',
    description: 'Marketing campaigns and brand strategy evaluation',
    icon: '/marketing.png',
    color: 'bg-blue-400'
  },
  {
    id: 'customer-support',
    title: 'Customer Support',
    description: 'Customer support and success metrics assessment',
    icon: '/customer.png',
    color: 'bg-purple-400'
  }
]

const RoleSelection: React.FC<RoleSelectionProps> = ({ onRoleSelect }) => {
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)

  const handleRoleClick = (role: Role) => {
    setSelectedRole(role)
  }

  const handleConnectToZayn = () => {
    if (selectedRole) {
      onRoleSelect(selectedRole)
    }
  }

  return (
    <div className="min-h-screen w-screen flex flex-col items-center justify-center p-4 sm:p-6 md:p-8 relative overflow-hidden">
      {/* Background Video */}
      <video 
        autoPlay 
        loop 
        muted 
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src="https://pub-b4a0d7160e804448b1c1062c499df6e5.r2.dev/background.mp4" type="video/mp4" />
      </video>
      
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-black/20"></div>
      
      
      {/* Content */}
      <div className="relative z-10 text-center mb-6 sm:mb-8 w-full max-w-6xl">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 sm:mb-4">Talk to Zayn</h1>
        <p className="text-white/80 text-sm sm:text-base md:text-lg px-4">Select a role to start your AI interview</p>
      </div>
      
      {/* Role Cards */}
      <div className="relative z-10 w-full max-w-6xl">
        <div className="bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
            {roles.map((role) => (
              <div
                key={role.id}
                className={`role-card p-4 sm:p-6 flex items-center gap-3 sm:gap-4 ${
                  selectedRole?.id === role.id ? 'ring-2 ring-white/50 bg-white/30' : ''
                }`}
                onClick={() => handleRoleClick(role)}
              >
                <div className="role-icon w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                  <img 
                    src={role.icon} 
                    alt={`${role.title} icon`}
                    className="w-full h-full object-contain"
                  />
                </div>
                
                <div className="flex-1 text-left min-w-0">
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800 mb-1 truncate">
                    {role.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                    {role.description}
                  </p>
                </div>
                
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
        
        <div className="text-center mt-4 sm:mt-6">
          {selectedRole && (
            <button
              onClick={handleConnectToZayn}
              className="bg-black hover:bg-gray-800 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 text-sm sm:text-base"
            >
              Connect to Zayn
            </button>
          )}
        </div>
      </div>
      
      {/* Bottom Text */}
      <div className="relative z-10 mt-6 sm:mt-8 text-center px-4">
        <p className="text-white/60 text-xs sm:text-sm">
          Powered by Getgeek • Real-time conversation • Professional interviews
        </p>
      </div>
    </div>
  )
}

export default RoleSelection
